import {
  TInstanceParticipant,
  TPairComparisonInstances,
  TPairComparisonSession,
} from "types/pairComparison";
import * as _ from "lodash";
import { TFormattedFeedbacks } from "types/feedbacks";
import * as XLSX from "xlsx";
import {
  TInstanceParticipantMC,
  TInstancesMC,
  TMultipleCriteriaSession,
} from "types/multipleCriteria";
import {
  TMultipleChannelInstances,
  TMultipleChannelSession,
} from "types/multipleChannel";
import {
  TManualChoiceInstances,
  TManualChoiceSession,
} from "types/manualChoice";

type TSession =
  | TPairComparisonSession
  | TMultipleCriteriaSession
  | TMultipleChannelSession
  | TManualChoiceSession;

type TInstancesTypes =
  | TPairComparisonInstances
  | TInstancesMC
  | TMultipleChannelInstances
  | TManualChoiceInstances;

export const getFeedbacksSheet = (
  session: TSession,
  instances: TInstancesTypes
) => {
  const feedbacks = getFeedbackAnswers(session, instances);
  return createFeedbacksSheet(feedbacks || []);
};

const createFeedbacksSheet = (feedbacksData: TFormattedFeedbacks) => {
  if (!feedbacksData || feedbacksData.length === 0) {
    return null;
  }

  // Créer un tableau de tableaux (array of arrays) pour Excel
  const excelRows: any[] = [];

  feedbacksData.forEach((questionGroup, groupIndex) => {
    if (questionGroup && questionGroup.answers.length > 0) {
      // Ajouter le titre de la question

      const questionTitle = questionGroup.feedback.question;
      excelRows.push([`${questionTitle}`]);
      excelRows.push([""]);

      // Ajouter les en-têtes de colonnes
      excelRows.push(["Participant", "Participant Name", "Answer"]);

      // Ajouter les réponses
      questionGroup.answers.forEach((response) => {
        excelRows.push([
          response.participantId,
          response.participantName,
          response.answer,
        ]);
      });

      excelRows.push([""]);
      excelRows.push([""]);
    }
  });

  const feedbacksSheet = XLSX.utils.aoa_to_sheet(excelRows as any);

  const columnConfig = {
    0: 25,
    1: 25,
    2: 80,
  };

  // Calculer les largeurs de colonnes
  const colWidths = [];
  for (let i = 0; i < 3; i++) {
    colWidths.push({
      width: columnConfig[i as keyof typeof columnConfig] || 25,
    });
  }

  feedbacksSheet["!cols"] = colWidths;

  return feedbacksSheet;
};

const getFeedbackAnswers = (session: TSession, instances: TInstancesTypes) => {
  const { metadata } = session;
  const { type } = metadata;

  const feedbacks =
    type === "PILOTED"
      ? getFeedbackAnswersForPilotedSession(session, instances)
      : getFeedbackAnswersForAutonomousSession(session, instances);

  return feedbacks;
};

const getFeedbackAnswersForPilotedSession = (
  session: TSession,
  instances: TInstancesTypes
): TFormattedFeedbacks => {
  return getFeedBacksFormatted(
    session,
    instances[0].feedbackAnswers,
    instances[0].participants
  );
};

const getFeedbackAnswersForAutonomousSession = (
  session: TSession,
  instances: TInstancesTypes
): TFormattedFeedbacks | null => {
  const resultsMerged = {};

  let participants: TInstanceParticipant[] | TInstanceParticipantMC[] = [];
  if (instances.length > 0) {
    instances.forEach((instance) => {
      if (instance.participants) {
        // @ts-ignore
        participants = participants.concat(instance?.participants);
      }
    });
  }

  // Merge feedback answers of each session instance
  instances.map((instance) =>
    instance.feedbackAnswers
      ? _.merge(resultsMerged, instance.feedbackAnswers)
      : null
  );

  if (!instances || instances.length === 0) return null;
  return getFeedBacksFormatted(session, resultsMerged, participants);
};

const getFeedBacksFormatted = (
  session: TSession,
  answers: any,
  participants: any
) => {
  const { data } = session;
  const { feedbacks = [] } = data;
  const formatedResults: TFormattedFeedbacks = [];

  if (!answers) return formatedResults;
  feedbacks.map((feedback) =>
    formatedResults.push({ feedback, answers: [], total: 0, average: 0 })
  );

  // For each participant feedbacks
  for (let participantId in answers) {
    const participantFeedbacks = answers[participantId];

    let participantName =
      participants?.find((part: any) => part.id === participantId)?.infos
        ?.name || null;
    // For each feedback
    for (let feedbackId in participantFeedbacks) {
      let answer = participantFeedbacks[feedbackId];
      if (answer === "Did not answer" || answer === "N'a pas répondu") {
        answer = "-";
      }
      const feedbackData = feedbacks.find((f) => f.id === feedbackId);

      // We add the answer to the list of answers
      if (formatedResults.find((result) => result.feedback.id === feedbackId)) {
        // @ts-ignore
        formatedResults
          .find((result) => result.feedback.id === feedbackId)
          .answers.push({ participantId, answer, participantName });
        // For closed feedback, we compute some calculation because the vote is done with number
        if (feedbackData?.type === "CLOSE" /** Use constants */) {
          // @ts-ignore
          const totalOfVotes = formatedResults.find(
            (result) => result.feedback.id === feedbackId
          ).answers.length;

          // @ts-ignore
          formatedResults.find(
            (result) => result.feedback.id === feedbackId
          ).total += answer;
          // @ts-ignore
          formatedResults.find(
            (result) => result.feedback.id === feedbackId
          ).average = _.round(
            // @ts-ignore
            formatedResults.find((result) => result.feedback.id === feedbackId)
              .total / totalOfVotes,
            2
          );
        }
      }
    }
  }

  return formatedResults;
};

export const getColumnWidths = (data: any) => {
  const headers = Object.keys(data[0] || {});

  return headers.map((header) => {
    let width = calculateColumnWidth(data, header);

    return { width };
  });
};

const calculateColumnWidth = (data: any, columnName: string) => {
  let maxWidth = columnName.length;

  data.forEach((row: any) => {
    const value = row[columnName];
    const cellValue = String(value ?? "");
    const lines = cellValue.split("\n");
    const maxLineLength = Math.max(...lines.map((line) => line.length));
    maxWidth = Math.max(maxWidth, maxLineLength);
  });

  return Math.min(maxWidth + 3, 100);
};

// Fonction générique pour calculer les statistiques (moyenne, médiane, écart-type)
export const calculateStatistics = (values: number[]) => {
  if (!values || values.length === 0) {
    return {
      average: 0,
      median: 0,
      standardDeviation: 0,
    };
  }

  // Calcul de la moyenne
  const average = values.reduce((sum, value) => sum + value, 0) / values.length;

  // Calcul de la médiane
  const sortedValues = [...values].sort((a, b) => a - b);
  const median =
    sortedValues.length % 2 === 0
      ? (sortedValues[sortedValues.length / 2 - 1] +
          sortedValues[sortedValues.length / 2]) /
        2
      : sortedValues[Math.floor(sortedValues.length / 2)];

  // Calcul de l'écart-type
  const variance =
    values.reduce((sum, value) => sum + Math.pow(value - average, 2), 0) /
    values.length;
  const standardDeviation = Math.sqrt(variance);

  return {
    average: Math.round(average * 100) / 100, // Arrondir à 2 décimales
    median: Math.round(median * 100) / 100, // Arrondir à 2 décimales
    standardDeviation: Math.round(standardDeviation * 100) / 100, // Arrondir à 2 décimales
  };
};

// Generic function to create a sequence statistics sheet
export const createSequenceStatisticsSheet = (
  sequenceStats: any[],
  headers: string[]
) => {
  if (!sequenceStats || sequenceStats.length === 0) {
    return null;
  }

  const excelRows = [];

  // Headers
  excelRows.push(headers);

  // Data
  sequenceStats.forEach((stat) => {
    const row = headers.map((header) => {
      switch (header) {
        case "Average":
          return stat.average;
        case "Median":
          return stat.median;
        case "Standard deviation":
          return stat.standardDeviation;
        default:
          // Handle property names with spaces or variations
          const propertyName = header.toLowerCase().replace(/\s+/g, "");
          return (
            stat[propertyName] ||
            stat[header] ||
            stat["imageName"] || // For MultipleChannel
            stat["channelName"] || // For other protocols
            stat["resourceName"] || // For other protocols
            ""
          );
      }
    });
    excelRows.push(row);
  });

  const statisticsSheet = XLSX.utils.aoa_to_sheet(excelRows);

  // Adjust column widths
  const colWidths = headers.map(() => ({ width: 20 }));
  statisticsSheet["!cols"] = colWidths;

  return statisticsSheet;
};

// Generic function to create a general information sheet
export const createInfoSheet = (
  sessionName: string,
  protocolType: string,
  sessionContext: string
) => {
  const excelRows = [];

  // Title
  excelRows.push(["Session Information"]);
  excelRows.push([]);

  // Information
  excelRows.push(["Session Name", sessionName]);
  excelRows.push(["Protocol Type", protocolType]);
  excelRows.push(["Session Context", sessionContext]);
  excelRows.push([]);
  excelRows.push(["Export Date", new Date().toLocaleDateString("en-US")]);

  const infoSheet = XLSX.utils.aoa_to_sheet(excelRows);

  // Adjust column widths
  infoSheet["!cols"] = [
    { width: 25 }, // Labels column
    { width: 50 }, // Values column
  ];

  return infoSheet;
};
